import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Plus, Save, Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { ASSET_DIMS, FOCAL_DEFAULTS } from "@/lib/focal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AdminMerchBlocks from "@/components/admin/AdminMerchBlocks";

const EMPTY_SLIDE = {
  image: "", mobile_image: "", video: "", layout: "auto", eyebrow: "", headline: "",
  subtext: "", cta: "", link: "", store: "", text_align: "left", text_colour: "light",
  scrim: 55, focal: null, text_side: "auto",
};
const RATIO_HINTS = {
  desktop: "Expected: wide landscape 21:9 (e.g. 2400×1024). Portrait assets switch the slide to the split layout automatically — never a centre crop.",
  mobile: "Expected: tall portrait 4:5 (e.g. 1080×1350). Shown on phones in place of the desktop image.",
};

function FocalClick({ src, focal, onPick, testid }) {
  const dims = ASSET_DIMS[src];
  const f = focal || FOCAL_DEFAULTS[src] || { x: 50, y: dims && dims[1] > dims[0] ? 33 : 50 };
  return (
    <div className="relative cursor-crosshair bg-neutral-100 rounded overflow-hidden"
      onClick={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        onPick({ x: Math.round(((e.clientX - r.left) / r.width) * 100), y: Math.round(((e.clientY - r.top) / r.height) * 100) });
      }}>
      <img src={src} alt="" className="w-full max-h-52 object-contain mx-auto" data-testid={testid} />
      <span className="absolute h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-brand bg-white/80 pointer-events-none"
        style={{ left: `${f.x}%`, top: `${f.y}%` }} data-testid={`${testid}-marker`} />
    </div>
  );
}

function CropPreviews({ src, focal }) {
  const f = focal || FOCAL_DEFAULTS[src] || { x: 50, y: 30 };
  const pos = `${f.x}% ${f.y}%`;
  const frames = [["Desktop hero · 21:9", "aspect-[21/9]"], ["Mobile hero · 4:5", "aspect-[4/5]"], ["Category tile · 1:1", "aspect-square"]];
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="crop-previews">
      {frames.map(([label, cls]) => (
        <div key={label}>
          <div className={`${cls} overflow-hidden rounded border border-neutral-200 bg-neutral-50`}>
            <img src={src} alt="" className="h-full w-full object-cover" style={{ objectPosition: pos }} />
          </div>
          <p className="text-[10px] font-mono text-neutral-500 mt-1 text-center">{label}</p>
        </div>
      ))}
    </div>
  );
}

export default function AdminHomepage() {
  const { config } = useStore();
  const [slides, setSlides] = useState([]);
  const [sections, setSections] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [focalPoints, setFocalPoints] = useState({});
  const [assets, setAssets] = useState([]);
  const [blocks, setBlocks] = useState({});
  const [productSlugs, setProductSlugs] = useState([]);

  useEffect(() => {
    if (!config) return;
    const hp = config.homepage || {};
    setSlides(JSON.parse(JSON.stringify(hp.slides || [])));
    setSections(JSON.parse(JSON.stringify(hp.sections || [])));
    setAnnouncements(JSON.parse(JSON.stringify(config.announcements || [])));
    setFocalPoints(config.focal_points || {});
    setBlocks({
      shoppable_videos: hp.shoppable_videos || {},
      lookbook: hp.lookbook || {},
      bundle_builder: hp.bundle_builder || {},
      image_compare: hp.image_compare || {},
      sticky_pdp_media: hp.sticky_pdp_media !== false,
    });
  }, [config]);

  useEffect(() => {
    api.get("/admin/media/assets", { params: { kind: "image", limit: 200 } }).then((r) => setAssets(r.data)).catch(() => {});
    api.get("/products", { params: { limit: 300 } }).then((r) => setProductSlugs((r.data.items || []).map((p) => ({ slug: p.slug, title: p.title })))).catch(() => {});
  }, []);

  const setSlide = (i, patch) => setSlides((s) => s.map((sl, j) => (j === i ? { ...sl, ...patch } : sl)));
  const moveSlide = (i, dir) => setSlides((s) => {
    const next = [...s];
    const j = i + dir;
    if (j < 0 || j >= next.length) return s;
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });

  const save = async () => {
    try {
      const hp = { ...(config.homepage || {}), slides, sections, ...blocks };
      const overrides = Object.fromEntries(Object.entries(focalPoints).filter(([k, v]) => {
        const d = FOCAL_DEFAULTS[k];
        return !d || d.x !== v.x || d.y !== v.y;
      }));
      await api.put("/admin/settings", { homepage: hp, announcements, focal_points: overrides });
      toast.success("Homepage saved");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const assetDims = Object.fromEntries(assets.map((a) => [a.url, [a.width || 0, a.height || 0]]));
  const dimsOf = (src) => ASSET_DIMS[src] || assetDims[src];
  const isPortraitAsset = (src) => { const d = dimsOf(src); return d && d[1] > d[0]; };

  return (
    <div className="space-y-6" data-testid="admin-homepage-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Homepage & Copy</h1>
          <p className="text-xs text-neutral-500 mt-1">Text is rendered as HTML over images — never baked into artwork. All copy here is editable.</p>
        </div>
        <Button onClick={save} className="bg-neutral-900 text-white" data-testid="homepage-save-btn"><Save size={14} className="mr-1" /> Save all</Button>
      </div>

      <section className="space-y-3" data-testid="hero-slides-editor">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-sm uppercase tracking-widest font-mono text-neutral-500">Hero slides</h2>
          <Button variant="outline" size="sm" onClick={() => setSlides((s) => [...s, { ...EMPTY_SLIDE }])} data-testid="add-slide-btn"><Plus size={13} className="mr-1" /> Add slide</Button>
        </div>
        {slides.map((s, i) => {
          const portrait = isPortraitAsset(s.image);
          const layout = s.layout === "auto" ? (portrait ? "split" : "full") : s.layout;
          return (
            <div key={i} className="bg-white border border-neutral-200 p-4 space-y-3" data-testid={`slide-editor-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono text-neutral-400">SLIDE {i + 1} · layout: {layout}{portrait && layout === "split" ? " (portrait — image keeps natural aspect)" : ""}</span>
                <div className="flex gap-1">
                  <button onClick={() => moveSlide(i, -1)} className="p-1 hover:bg-neutral-100" data-testid={`slide-up-${i}`}><ArrowUp size={13} /></button>
                  <button onClick={() => moveSlide(i, 1)} className="p-1 hover:bg-neutral-100" data-testid={`slide-down-${i}`}><ArrowDown size={13} /></button>
                  <button onClick={() => setSlides((sl) => sl.filter((_, j) => j !== i))} className="p-1 hover:bg-neutral-100 text-red-500" data-testid={`slide-delete-${i}`}><Trash2 size={13} /></button>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label>Desktop image</Label>
                  <Input value={s.image} onChange={(e) => setSlide(i, { image: e.target.value })} list="hp-assets" className="bg-white" data-testid={`slide-image-${i}`} />
                  <p className="text-[10px] text-neutral-400 mt-1">{RATIO_HINTS.desktop}</p>
                </div>
                <div>
                  <Label>Mobile image (optional)</Label>
                  <Input value={s.mobile_image || ""} onChange={(e) => setSlide(i, { mobile_image: e.target.value })} list="hp-assets" className="bg-white" data-testid={`slide-mobile-image-${i}`} />
                  <p className="text-[10px] text-neutral-400 mt-1">{RATIO_HINTS.mobile}</p>
                </div>
              </div>
              <datalist id="hp-assets">
                {Object.keys(ASSET_DIMS).map((k) => <option key={k} value={k} />)}
                {assets.map((a) => <option key={a.id} value={a.url}>{a.name}</option>)}
              </datalist>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div><Label>Eyebrow (optional)</Label><Input value={s.eyebrow || ""} onChange={(e) => setSlide(i, { eyebrow: e.target.value })} className="bg-white" data-testid={`slide-eyebrow-${i}`} /></div>
                <div className="lg:col-span-2"><Label>Headline</Label><Input value={s.headline || ""} onChange={(e) => setSlide(i, { headline: e.target.value })} className="bg-white" data-testid={`slide-headline-${i}`} /></div>
                <div><Label>Store scope</Label>
                  <select value={s.store || ""} onChange={(e) => setSlide(i, { store: e.target.value })} className="w-full h-9 border border-neutral-300 bg-white px-2 text-sm" data-testid={`slide-store-${i}`}>
                    <option value="">All stores</option><option value="silver">Silver</option><option value="gold">Gold</option><option value="coins">Coins</option>
                  </select>
                </div>
              </div>
              <div><Label>Subheadline</Label><Input value={s.subtext || ""} onChange={(e) => setSlide(i, { subtext: e.target.value })} className="bg-white" data-testid={`slide-subtext-${i}`} /></div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div><Label>CTA label</Label><Input value={s.cta || ""} onChange={(e) => setSlide(i, { cta: e.target.value })} className="bg-white" data-testid={`slide-cta-${i}`} /></div>
                <div><Label>CTA link</Label><Input value={s.link || ""} onChange={(e) => setSlide(i, { link: e.target.value })} className="bg-white" data-testid={`slide-link-${i}`} /></div>
                <div><Label>Text alignment</Label>
                  <select value={s.text_align || "left"} onChange={(e) => setSlide(i, { text_align: e.target.value })} className="w-full h-9 border border-neutral-300 bg-white px-2 text-sm" data-testid={`slide-align-${i}`}>
                    <option value="left">Left</option><option value="centre">Centre</option><option value="right">Right</option>
                  </select>
                </div>
                <div><Label>Text colour</Label>
                  <select value={s.text_colour || "light"} onChange={(e) => setSlide(i, { text_colour: e.target.value })} className="w-full h-9 border border-neutral-300 bg-white px-2 text-sm" data-testid={`slide-colour-${i}`}>
                    <option value="light">Light (on dark image)</option><option value="dark">Dark (on light image)</option>
                  </select>
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-3 items-end">
                <div><Label>Scrim opacity ({s.scrim ?? 55}%)</Label>
                  <input type="range" min="0" max="80" value={s.scrim ?? 55} onChange={(e) => setSlide(i, { scrim: Number(e.target.value) })} className="w-full" data-testid={`slide-scrim-${i}`} />
                </div>
                <div><Label>Layout</Label>
                  <select value={s.layout || "auto"} onChange={(e) => setSlide(i, { layout: e.target.value })} className="w-full h-9 border border-neutral-300 bg-white px-2 text-sm" data-testid={`slide-layout-${i}`}>
                    <option value="auto">Auto (split when portrait)</option><option value="split">Split — text beside image</option><option value="full">Full-bleed — text over image</option>
                  </select>
                </div>
                <div><Label>Text side</Label>
                  <select value={s.text_side || "auto"} onChange={(e) => setSlide(i, { text_side: e.target.value })} className="w-full h-9 border border-neutral-300 bg-white px-2 text-sm" data-testid={`slide-text-side-${i}`}>
                    <option value="auto">Auto (opposite the focal point)</option><option value="left">Left</option><option value="right">Right</option>
                  </select>
                </div>
              </div>

              {s.image && (
                <div className="grid md:grid-cols-2 gap-4 border-t border-neutral-100 pt-3">
                  <div>
                    <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-1">Focal point — click the subject (face / jewellery)</p>
                    <FocalClick src={s.image} focal={s.focal} onPick={(f) => setSlide(i, { focal: f })} testid={`slide-focal-${i}`} />
                    <p className="text-[10px] text-neutral-400 mt-1">Crops anchor here. Text is placed on the opposite side so it never covers the subject.</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-1">Crop check — every breakpoint</p>
                    <CropPreviews src={s.image} focal={s.focal} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </section>

      <section className="bg-white border border-neutral-200 p-4" data-testid="sections-editor">
        <h2 className="font-semibold text-sm uppercase tracking-widest font-mono text-neutral-500 mb-3">Section headings</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {sections.map((sec, i) => (
            <div key={sec.id} className="flex items-center gap-2">
              <input type="checkbox" checked={sec.enabled !== false}
                onChange={(e) => setSections((list) => list.map((x, j) => (j === i ? { ...x, enabled: e.target.checked } : x)))}
                data-testid={`section-enabled-${sec.id}`} />
              <div className="flex-1">
                <Input value={sec.title || ""} onChange={(e) => setSections((list) => list.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))}
                  className="h-8 bg-white text-xs" data-testid={`section-title-${sec.id}`} />
                <p className="text-[10px] font-mono text-neutral-400">{sec.id}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <AdminMerchBlocks blocks={blocks} setBlocks={setBlocks} productSlugs={productSlugs} assets={assets} />

      <section className="bg-white border border-neutral-200 p-4" data-testid="announcements-editor">
        <h2 className="font-semibold text-sm uppercase tracking-widest font-mono text-neutral-500 mb-1">Announcement bar (rotating)</h2>
        <p className="text-[11px] text-neutral-400 mb-3">A message marked “BIS” only shows while the BIS registration field is populated in Settings → Legal.</p>
        <div className="space-y-2">
          {announcements.map((a, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`announcement-row-${i}`}>
              <select value={a.icon || ""} onChange={(e) => setAnnouncements((l) => l.map((x, j) => (j === i ? { ...x, icon: e.target.value } : x)))}
                className="h-9 border border-neutral-300 bg-white px-2 text-xs" data-testid={`announcement-icon-${i}`}>
                <option value="">No icon</option><option value="truck">Truck</option><option value="percent">Percent</option><option value="badge">Badge</option>
              </select>
              <Input value={a.text} onChange={(e) => setAnnouncements((l) => l.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))}
                className="bg-white" data-testid={`announcement-text-${i}`} />
              <label className="text-xs flex items-center gap-1 shrink-0">
                <input type="checkbox" checked={!!a.requires_bis}
                  onChange={(e) => setAnnouncements((l) => l.map((x, j) => (j === i ? { ...x, requires_bis: e.target.checked } : x)))}
                  data-testid={`announcement-bis-${i}`} /> BIS
              </label>
              <button onClick={() => setAnnouncements((l) => l.filter((_, j) => j !== i))} className="text-red-500 p-1" data-testid={`announcement-remove-${i}`}><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
        <Button variant="outline" size="sm" className="mt-2" onClick={() => setAnnouncements((l) => [...l, { icon: "", text: "" }])} data-testid="announcement-add-btn"><Plus size={13} className="mr-1" /> Add message</Button>
      </section>

      <section className="bg-white border border-neutral-200 p-4" data-testid="focal-points-editor">
        <h2 className="font-semibold text-sm uppercase tracking-widest font-mono text-neutral-500 mb-1">Campaign asset focal points</h2>
        <p className="text-[11px] text-neutral-400 mb-3">Every tile, card and banner crops around these points. Click the subject to move a point.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {Object.keys(FOCAL_DEFAULTS).map((src) => (
            <div key={src}>
              <FocalClick src={src} focal={focalPoints[src]} onPick={(f) => setFocalPoints((fp) => ({ ...fp, [src]: f }))} testid={`focal-${src.split("/").pop().replace(/\W/g, "-")}`} />
              <p className="text-[10px] font-mono text-neutral-500 mt-1 truncate">{src.split("/").pop()}</p>
              <CropPreviews src={src} focal={focalPoints[src]} />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
